// /**
//  * Orders - list
//  */

// React
import React, {useContext, useState} from 'react';
import {Link} from 'react-router-dom';

// Context
import AuthContext from '../../authContext';

// UI
import theme from '../../theme';
import clsx from 'clsx';
import {
  createStyles,
  makeStyles,
  Theme,
  withStyles,
  lighten,
} from '@material-ui/core/styles';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  IconButton,
  Button,
  Checkbox,
  Typography,
  Tooltip,
  Toolbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  Select,
  CircularProgress,
  Grid,
} from '@material-ui/core';

// Icon
import Icon from '@mdi/react';
import {
  mdiClose,
  mdiFileDocumentOutline,
  mdiPackageVariantClosed,
  mdiPencilOutline,
  mdiHistory,
  mdiChevronDown,
  mdiChevronUp,
  mdiAccountArrowLeftOutline, mdiCubeSend, mdiBarcode, mdiPlusBoxMultiple,
} from '@mdi/js';

// Components
import Can from '../../components/Can';

// Rules
import {
  rulesAdminChangeOrderStatus,
  rulesOrderStatus,
} from '../../utils/rulesOrderStatus';

// Helper
import {translateOrderStatus, checkMatchMedia} from '../../utils/helpers';
import jMoment from 'moment-jalaali';

// Type
import {Order, Status, PostOrder} from '../orders/interfaces';

type Data = PostOrder;
type Props = {
  data: any;
  handleConfirmCanceleBox: (id: number) => void;
  changeStatusOrderWhitId: any;
  handleOpenConfirmUpdateState: any;
};
type Ord = 'asc' | 'desc';

interface HeadCell {
  id: keyof Data;
  label: string;
}

interface EnhancedTableProps {
  classes: ReturnType<typeof useStyles>;
  onRequestSort: (
    event: React.MouseEvent<unknown>,
    property: keyof Data
  ) => void;
  order: Ord;
  orderBy: string;
  rowCount: number;
  numSelected: number;
  onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

// Style
const StyledTableCell = withStyles((theme: Theme) =>
  createStyles({
    head: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      '@media (max-width: 768px)': {
        fontSize: 10,
      },
    },
    body: {
      fontSize: 14,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  })
)(TableCell);
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: '100%',
    },
    loading: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      height: '50vh',
      alignSelf: 'center',
      justifyContent: 'center',
      background: '#ccc',
    },
    paper: {
      width: '100%',
    },
    table: {
      // minWidth: 750,
    },
    head: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
    },
    columnText: {
      padding: '0 10px',
    },
    visuallyHidden: {
      border: 0,
      clip: 'rect(0 0 0 0)',
      height: 1,
      margin: -1,
      overflow: 'hidden',
      padding: 0,
      position: 'absolute',
      top: 20,
      width: 1,
    },
    container: {
      maxHeight: 650,
      borderRadius: '4px',
    },
    // Fully visible for active icons
    activeSortIcon: {
      opacity: 1,
      color: `${theme.palette.secondary.main}!important`,
    },
    // Half visible for inactive icons
    inactiveSortIcon: {
      opacity: 0.4,
    },
    iconButton: {
      padding: 10,
      position: 'relative',
      '&:before': {
        content: '""',
        position: 'absolute',
        width: '1px',
        height: '36px',
        top: '4px',
        left: '-9px',
        backgroundColor: '#EDF1F4',
      },
    },
    changStatusIcon: {
      marginRight: '10px',
      position: 'absolute',
    },
    changStatusBox: {
      display: 'flex',
      textAlign: 'left',

      fontWeight: 400,
      fontSize: '14px',
    },
    renderValue: {
      paddingLeft: '32px',
    },
    radioGroup: {
      margin: '-8px 0',
    },
    radioGroupItem: {
      borderBottom: '1px solid #D8D8D8',
      marginRight: '-8px',
      padding: '8px 16px',
      '&:last-child': {
        borderBottom: '0px',
      },
    },
  })
);

const useToolbarStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(1),
      marginBottom: theme.spacing(1),
      justifyContent: 'space-between',
    },
    highlight:
      theme.palette.type === 'light'
        ? {
            color: theme.palette.secondary.main,
            backgroundColor: lighten(theme.palette.secondary.light, 0.85),
          }
        : {
            color: theme.palette.text.primary,
            backgroundColor: theme.palette.secondary.dark,
          },
    title: {
      // flex: '1 1 100%',
    },
  })
);

interface EnhancedTableToolbarProps {
  numSelected: number;
  setOpenChangeStatus: any;
  selected: any;
  data: any;
}

const EnhancedTableToolbar = (props: EnhancedTableToolbarProps) => {
  const classes = useToolbarStyles();
  const [openAlertWarning, setOpenAlertWarning] = useState(false as boolean);
  const {numSelected, selected, data} = props;

  const handleClickChangeStatus = () => {
    const selectedData = data.filter(
      (item: any) => selected.indexOf(item.id) > -1
    );

    if (!checkStatus(selectedData)) {
      setOpenAlertWarning(true);
    } else {
      props.setOpenChangeStatus(true);
    }
  };

  const checkStatus = (data: any) => {
    let sameStatus = true;
    data?.map((item: any) => {
      if (item?.status !== data[0]?.status) {
        return (sameStatus = false);
      }
      return item;
    });
    return sameStatus;
  };

  return (
    <>
      {numSelected > 0 && (
        <Toolbar
          className={clsx(classes.root, {
            [classes.highlight]: numSelected > 0,
          })}
        >
          {numSelected > 0 ? (
            <Typography
              className={classes.title}
              color="inherit"
              variant="subtitle1"
              component="div"
            >
              {numSelected} انتخاب
            </Typography>
          ) : (
            <Typography
              className={classes.title}
              variant="h6"
              id="tableTitle"
              component="div"
            />
          )}
        </Toolbar>
      )}
      <Dialog
        fullWidth
        open={openAlertWarning}
        onClose={() => setOpenAlertWarning(false)}
        aria-labelledby="responsive-dialog-title"
      >
        <DialogTitle>امکان تغییر وضعیت سفارش وجود ندارد </DialogTitle>
        <DialogContent>
          وضعیت سفارش آیتم‌های انتخاب شده باید یکی باشد
        </DialogContent>
        <DialogActions>
          <Button
            autoFocus
            onClick={() => setOpenAlertWarning(false)}
            variant="contained"
            color="primary"
          >
            {'متوجه شدم'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator<Key extends keyof any>(
  order: Ord,
  orderBy: Key
): (
  a: {[key in Key]: number | string},
  b: {[key in Key]: number | string}
) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort<T>(array: T[], comparator: (a: T, b: T) => number) {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map(el => el[0]);
}

function EnhancedTableHead(props: EnhancedTableProps) {
  const authContext = useContext(AuthContext);
  const classes = useStyles();
  const {
    order,
    orderBy,
    onRequestSort,
    numSelected,
    rowCount,
    onSelectAllClick,
  } = props;
  const createSortHandler = (property: keyof Data) => (
    event: React.MouseEvent<unknown>
  ) => {
    onRequestSort(event, property);
  };
  let headCells: HeadCell[] = [];

  Can({
    role: authContext.user.role,
    perform: 'orders:list:centerName',
    yes: () => {
      headCells = [
        {
          id: 'customerName',
          label: 'نام سفارش‌دهنده',
        },
        {id: 'partnerName', label: 'شرکت'},
        {id: 'id', label: 'کد سفارش'},
        {id: 'centerName', label: 'مرکز سفارش'},
        {
          id: 'deliveryDate',
          label: 'زمان تحویل',
        },
        {id: 'status', label: 'وضعیت سفارش'},
      ];
    },
    no: () => {
      headCells = [
        {id: 'id', label: 'شناسه سفارش'},
        {id: 'partnerName', label: 'شرکت'},
        {
          id: 'customerName',
          label: 'سفارش‌دهنده',
        },
        {
          id: 'dimensions',
          label: 'ابعاد',
        },
        {
          id: 'weight',
          label: 'وزن',
        },
        {
          id: 'deliveryDate',
          label: 'تاریخ تحویل',
        },
        {id: 'status', label: 'وضعیت'},
      ];
    },
  });

  return (
    <TableHead>
      <TableRow>
        <StyledTableCell padding="checkbox">
          <Can
            role={authContext.user.role}
            perform="orders:changeOrderStatus"
            yes={() => (
              <Checkbox
                indeterminate={numSelected > 0 && numSelected < rowCount}
                checked={rowCount > 0 && numSelected === rowCount}
                onChange={onSelectAllClick}
                inputProps={{'aria-label': 'select all desserts'}}
                style={{color: '#fff'}}
              />
            )}
          />
        </StyledTableCell>
        {headCells.map(headCell => (
          <StyledTableCell
            key={headCell.id}
            align="center"
            sortDirection={orderBy === headCell.id ? order : false}
            data-cy={headCell.id}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
              classes={{
                // Override with the active class if this is the selected column or inactive otherwise
                icon:
                  orderBy === headCell.id
                    ? classes.activeSortIcon
                    : classes.inactiveSortIcon,
              }}
              IconComponent={() => (
                <Icon
                  path={
                    orderBy === headCell.id && order === 'desc'
                      ? mdiChevronDown
                      : mdiChevronUp
                  }
                  size={0.7}
                  className={
                    orderBy === headCell.id
                      ? classes.activeSortIcon
                      : classes.inactiveSortIcon
                  }
                />
              )}
            >
              <span className={classes.columnText}>{headCell.label}</span>
            </TableSortLabel>
          </StyledTableCell>
        ))}
        <StyledTableCell />
      </TableRow>
    </TableHead>
  );
}

export default function EnhancedTable(props: Props) {
  const authContext = useContext(AuthContext);
  const classes = useStyles();

  const [order, setOrder] = React.useState<Ord>('desc');
  const [orderBy, setOrderBy] = React.useState<keyof Data>('id');
  const [selected, setSelected] = React.useState<string[]>([]);
  const [valueStatus, setValueStatus] = React.useState('');
  const [openChangeStatus, setOpenChangeStatus] = React.useState(false);
  const [selectedOperator, setSelectedOperator] = useState(0);

  const handleChangeValueStatus = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setValueStatus((event.target as HTMLInputElement).value);
  };
  const {handleConfirmCanceleBox: handleClickOpen} = props;
  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof Data
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectOperator = (event: {target: HTMLInputElement}) => {
    const {target} = event;
    setSelectedOperator(+target.value);
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelecteds = props.data.map((n: any) => n.id);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };
  const handleClick = (event: React.MouseEvent<unknown>, id: string) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected: string[] = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }

    setSelected(newSelected);
  };

  const changeStatusOrder = () => {
    props.changeStatusOrderWhitId({
      ids: selected,
      operatorId: selectedOperator,
      description: null,
    });
  };

  const isSelected = (id: string) => selected.indexOf(id) !== -1;

  const HtmlTooltip = withStyles((theme: Theme) => ({
    tooltip: {
      backgroundColor: '#f5f5f9',
      color: 'rgba(0, 0, 0, 0.87)',
      maxWidth: 220,
      fontSize: theme.typography.pxToRem(12),
      border: '1px solid #dadde9',
    },
  }))(Tooltip);

  return (
    <div className={classes.root}>
      <EnhancedTableToolbar
        numSelected={selected.length}
        setOpenChangeStatus={setOpenChangeStatus}
        selected={selected}
        data={props.data}
      />
      <Paper className={classes.paper}>
        <TableContainer className={classes.container}>
          <Table
            stickyHeader
            className={classes.table}
            aria-labelledby="tableTitle"
            size={checkMatchMedia('(max-width: 1024px)') ? 'small' : 'medium'}
            aria-label="enhanced table"
          >
            <EnhancedTableHead
              classes={classes}
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              rowCount={props.data.length}
              numSelected={selected.length}
              onSelectAllClick={handleSelectAllClick}
            />

            <TableBody>
              {stableSort(props.data, getComparator(order, orderBy))
                .slice()
                .map((row: any, index: number) => {
                  const labelId = `enhanced-table-checkbox-${index}`;
                  const isItemSelected = isSelected(row.id);
                  let color = '';
                  let backgroundColor = '';

                  switch (true) {
                    case row.status === 'UNLOADED' ||
                      row.status === 'PACKAGED' ||
                      row.status === 'BUNDLED' ||
                      row.status === 'ASSIGNED':
                      backgroundColor = '#d1e8fa';
                      color = '#283c74';
                      break;
                    case row.status === 'CANCELLED' ||
                      row.status === 'RETURNING':
                      backgroundColor = '#fdd1cc';
                      color = '#611a15';
                      break;
                    case row.status === 'LOADED' || row.status === 'DELIVERED':
                      backgroundColor = '#d7fcd7';
                      color = '#1e4659';
                      break;
                  }

                  return (
                    <TableRow
                      key={index}
                      hover
                      // onClick={(event) => handleClick(event, row.id)}
                      role="checkbox"
                      style={{backgroundColor: backgroundColor}}
                      aria-checked={isItemSelected}
                      tabIndex={-1}
                      selected={isItemSelected}
                    >
                      <StyledTableCell padding="checkbox">
                        <Can
                          role={authContext.user.role}
                          perform="orders:changeOrderStatus"
                          yes={() => (
                            <Checkbox
                              key={index}
                              onClick={event => handleClick(event, row.id)}
                              checked={isItemSelected}
                              inputProps={{'aria-labelledby': labelId}}
                            />
                          )}
                        />
                      </StyledTableCell>
                      <StyledTableCell
                        align="center"
                        style={{color: color}}
                        data-cy="row-id"
                      >
                        {row.id}
                      </StyledTableCell>
                      <StyledTableCell
                        id={labelId}
                        scope="row"
                        align="center"
                        style={{color: color}}
                      >
                        {row.partnerName}
                      </StyledTableCell>
                      <StyledTableCell
                        id={labelId}
                        scope="row"
                        align="center"
                        style={{color: color}}
                      >
                        {row.customerName}
                      </StyledTableCell>
                      <StyledTableCell
                        id={labelId}
                        scope="row"
                        align="center"
                        style={{color: color}}
                      >
                        {row.packages?.length > 1 ? (
                          <HtmlTooltip
                            title={
                              <React.Fragment>
                                {row.packages.map((itemPackage: any) => {
                                  return (
                                    <p style={{color: color, direction: 'ltr'}}>
                                      {itemPackage.length} x {itemPackage.width}{' '}
                                      x {itemPackage.height}
                                    </p>
                                  );
                                })}
                              </React.Fragment>
                            }
                          >
                            <Button>
                              <Grid
                                style={{flexWrap: 'nowrap'}}
                                container
                                alignItems="center"
                                spacing={2}
                              >
                                <span>
                                  {`${row.packages[0].length} x ${row.packages[0].width} x ${row.packages[0].height}`}
                                </span>
                                {row.packages.length > 1 && (
                                  <span style={{padding: '5px'}}>
                                    <Icon
                                      color={theme.palette.green.main}
                                      path={mdiPlusBoxMultiple}
                                      size={1}
                                    />
                                  </span>
                                )}
                              </Grid>
                            </Button>
                          </HtmlTooltip>
                        ) : row.packages?.length === 1 ? (
                          <span>
                            {`${row.packages[0].length} x ${row.packages[0].width} x ${row.packages[0].height}`}
                          </span>
                        ) : (
                          '-'
                        )}
                      </StyledTableCell>
                      <StyledTableCell
                        id={labelId}
                        scope="row"
                        align="center"
                        style={{color: color}}
                      >
                        {row.weight}
                      </StyledTableCell>
                      {/* <TableCell align="center" style={{color: color}}>{row.trackingCode}</TableCell> */}
                      {/* <StyledTableCell align="center" style={{color: color}}>
                        {row.customerNationalCode}
                      </StyledTableCell> */}
                      {/*                      <Can
                        role={authContext.user.role}
                        perform="orders:list:centerName"
                        yes={() => (
                          <StyledTableCell align="center" style={{color: color}}>
                            {row.centerName}
                          </StyledTableCell>
                        )}
                      />*/}

                      <StyledTableCell align="center" style={{color: color}}>
                        <span>
                          {jMoment(row.deliveryDate, 'YYYY/M/D')
                            .locale('fa')
                            .format('jYY/jMM/jDD')}
                        </span>
                        &nbsp; &nbsp;- &nbsp; &nbsp;
                        <span>
                          {`${jMoment(row.deliveryStartTime, 'HH:mm:ss')
                            .locale('fa')
                            .format('HH')}
                        `}
                          {`تا ${jMoment(row.deliveryEndTime, 'HH:mm:ss')
                            .locale('fa')
                            .format('HH')}`}
                        </span>
                      </StyledTableCell>
                      <StyledTableCell align="center" style={{color: color}}>
                        <Can
                          role={authContext.user.role}
                          perform="orders:changeOrderStatus"
                          yes={() => (
                            <FormControl style={{width: '100%'}}>
                              <Icon
                                path={mdiPencilOutline}
                                size={1}
                                className={classes.changStatusIcon}
                              />
                              <Select
                                className={classes.changStatusBox}
                                fullWidth
                                name={`status${index}`}
                                multiple
                                id={row.rubikaId}
                                value={[{}]}
                                IconComponent={() => null}
                                renderValue={() => (
                                  <span className={classes.renderValue}>
                                    {translateOrderStatus(row.status)}
                                  </span>
                                )}
                              >
                                <RadioGroup
                                  className={classes.radioGroup}
                                  aria-label={`status${index}`}
                                  name={`status${index}`}
                                  value={'item'}
                                  onChange={(
                                    e: React.ChangeEvent<{value: unknown}>
                                  ) =>
                                    props.handleOpenConfirmUpdateState({
                                      id: row.id,
                                      body: `${e.target.value}`,
                                    })
                                  }
                                >
                                  {rulesOrderStatus[row.status]?.map(
                                    (item: any, index: number) => (
                                      <FormControlLabel
                                        className={classes.radioGroupItem}
                                        key={index}
                                        checked={row.status === item}
                                        value={item}
                                        disabled={
                                          rulesAdminChangeOrderStatus[
                                            authContext.user.role
                                          ].indexOf(item) === -1
                                        }
                                        control={<Radio />}
                                        label={translateOrderStatus(item)}
                                      />
                                    )
                                  )}
                                </RadioGroup>
                              </Select>
                            </FormControl>
                          )}
                          no={() => {
                            return (
                              <span>{translateOrderStatus(row.status)}</span>
                            );
                          }}
                        />
                      </StyledTableCell>
                      <StyledTableCell align="right">
                        <div>
                          <Can
                            role={authContext.user.role}
                            perform="orders:cancelOrders"
                            yes={() => (
                              <IconButton
                                aria-label="cancele"
                                title={
                                  row.status === 'CANCELLED'
                                    ? 'لغو شده'
                                    : 'لغو سفارش'
                                }
                                size="small"
                                style={{pointerEvents: 'fill'}}
                                disabled={row.status === 'CANCELLED'}
                                onClick={() => handleClickOpen(row.id)}
                              >
                                <Icon
                                  path={mdiClose}
                                  size={1}
                                  color={
                                    row.status !== 'CANCELLED'
                                      ? theme.palette.error.main
                                      : theme.palette.gray.main
                                  }
                                />
                              </IconButton>
                            )}
                          />
                        </div>
                      </StyledTableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </div>
  );
}
